import React, { useEffect, useState, useMemo } from 'react';
import { useItems } from '../contexts/ItemsContext';
import { useUser } from '../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import './Programs.css';
import { getFilters } from '../api/itemsApi';
import ProductCard from '../components/ProductCard';
import PageHeader from '../components/PageHeader';

const ItemCard = ({
  item,
  onOpen,
  onToggleFavorite,
  isFavorite,
  showMinitsLabel
}) => {
  const front =
    (item.files || []).find(
      f => f.for_show && f.type === 'image'
    ) ||
    (item.files || []).find(
      f => f.type === 'image'
    );

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    onToggleFavorite(item);
  };

  return (
    <button
      className="program-card"
      onClick={() => onOpen(item)}
    >
      <div className="program-card-border">

        {showMinitsLabel && (
          <div className="minits-label">
            minutes
          </div>
        )}

        <div className="program-card-media">

          {front ? (
            <img
              src={front.url}
              alt={item.name}
            />
          ) : (
            <div className="program-card-placeholder">
              אין תמונה
            </div>
          )}

          <button
            className={`favorite-btn ${
              isFavorite ? 'favorite-active' : ''
            }`}
            onClick={handleFavoriteClick}
            aria-label={
              isFavorite ? 'בסוף לא' : 'אהבתי'
            }
          >
            {isFavorite ? '❤️' : '🤍'}
          </button>

        </div>

        <div className="program-card-footer">

          <div className="program-card-name">
            {item.name}
          </div>

          <div className="program-card-meta">
            ₪ {item.price}
          </div>

          <div className="program-card-meta">
            {item.much_for}
          </div>

          {item.video && (
            <div className="program-card-video">
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0cad5e"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10" />
                <polygon points="10,8 16,12 10,16" />
              </svg>
            </div>
          )}

        </div>
      </div>
    </button>
  );
};


// נציג גם תוכניות וגם מיניטס
const GENRES = ['תוכנית'];


// רשימת פילטרים ידנית לעריכה
const FILTER_TAGS = [
  "חנוכה",
  "ט\"ו בשבט",
  "פורים",
  "משפחתי",
  "יומולדת",
  "בת מצווה",
  "תחרותי"
];


const Programs = () => {

  const {
    itemsByGenre,
    loadItems,
    refreshItems,
    loadingByGenre,
    errorByGenre
  } = useItems();

  const {
    user,
    addFavorite,
    removeFavorite
  } = useUser();

  const navigate = useNavigate();

  // שימוש במערך קבוע GENRES
  const genres = GENRES;


  // --------------------------------------------------
  // איסוף כל הפריטים
  // --------------------------------------------------

  const items = genres.flatMap(
    g => itemsByGenre[g] || []
  );

  const loading = genres.some(
    g => loadingByGenre[g]
  );

  const error = genres
    .map(g => errorByGenre[g])
    .find(Boolean);


  // --------------------------------------------------
  // Filter state
  // --------------------------------------------------

  const [options, setOptions] = useState({
    crowds: [],
    timings: [],
    types: []
  });

  const [filtersLoading, setFiltersLoading] =
    useState(false);

  const [filtersError, setFiltersError] =
    useState(null);

  const [selectedTags, setSelectedTags] =
    useState([]);

  const [selectedVideo, setSelectedVideo] =
    useState(null);


  // null = אין בחירה ידנית,
  // ולכן נשתמש אוטומטית בטווח המלא
  const [priceRange, setPriceRange] =
    useState(null);


  // --------------------------------------------------
  // חישוב המחיר הנמוך והגבוה ביותר
  // --------------------------------------------------

  const priceBounds = useMemo(() => {

    const prices = items
      .map(item => Number(item.price))
      .filter(price => Number.isFinite(price));

    // אם אין עדיין מוצרים
    if (prices.length === 0) {
      return [0, 0];
    }

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    return [
      minPrice,
      maxPrice
    ];

  }, [items]);


  // --------------------------------------------------
  // טווח המחירים הנוכחי
  // --------------------------------------------------

  const currentPriceRange =
    priceRange || priceBounds;


  // --------------------------------------------------
  // חישוב מיקום ה-highlight
  // --------------------------------------------------

  const highlightStyle = useMemo(() => {

    const containerWidth = 260;

    const marginLeft = 9;
    const marginRight = 9;

    const availableWidth =
      containerWidth -
      marginLeft -
      marginRight;


    const rangeMin =
      priceBounds[0];

    const rangeMax =
      priceBounds[1];


    const currentMin =
      currentPriceRange[0];

    const currentMax =
      currentPriceRange[1];


    // מניעת חלוקה באפס
    const rangeSize =
      Math.max(
        rangeMax - rangeMin,
        1
      );


    const minRatio =
      (currentMin - rangeMin) /
      rangeSize;

    const maxRatio =
      (currentMax - rangeMin) /
      rangeSize;


    // RTL
    const minPositionReversed =
      marginLeft +
      ((1 - minRatio) * availableWidth);

    const maxPositionReversed =
      marginLeft +
      ((1 - maxRatio) * availableWidth);


    const leftPx =
      maxPositionReversed;

    const widthPx =
      minPositionReversed -
      maxPositionReversed;


    return {
      left: `${leftPx}px`,
      width: `${Math.max(widthPx, 0)}px`
    };

  }, [
    priceBounds,
    currentPriceRange
  ]);


  // --------------------------------------------------
  // טעינת המוצרים
  // --------------------------------------------------

  useEffect(() => {

    genres.forEach(g => {
      loadItems(g);
    });

  }, []);


  // --------------------------------------------------
  // פתיחת מוצר
  // --------------------------------------------------

  const openItem = (item) => {
    navigate(`/item/${item.id}`);
  };


  // --------------------------------------------------
  // בדיקה האם מוצר מועדף
  // --------------------------------------------------

  const isItemFavorite = (item) => {

    if (!user?.likes) {
      return false;
    }

    return user.likes.some(
      like => like.id === item.id
    );
  };


  // --------------------------------------------------
  // הוספה / הסרה ממועדפים
  // --------------------------------------------------

  const toggleFavorite = async (item) => {

    if (!user) {
      return;
    }

    if (isItemFavorite(item)) {

      await removeFavorite(item.id);

    } else {

      await addFavorite(item);

    }
  };


  // --------------------------------------------------
  // טעינת הפילטרים
  // --------------------------------------------------

  useEffect(() => {

    let cancelled = false;

    let abortController =
      new AbortController();


    setOptions({
      crowds: [],
      timings: [],
      types: []
    });

    setFiltersLoading(true);
    setFiltersError(null);


    const tryFetch = async () => {

      try {

        // קריאה ללא signal,
        // כי getFilters לא תומך בפרמטרים
        const json =
          await getFilters();


        if (cancelled) {
          return;
        }


        const mk = (arr) => {

          if (!Array.isArray(arr)) {
            return [];
          }

          return arr.map(v => {

            if (typeof v === 'object') {
              return v.name || String(v);
            }

            return String(v);

          });

        };


        setOptions({
          crowds: mk(json.crowds),
          timings: mk(json.timings),
          types: mk(json.types)
        });

        setFiltersLoading(false);

      } catch (e) {

        if (!cancelled) {

          setOptions({
            crowds: [],
            timings: [],
            types: []
          });

          setFiltersError(
            e.message || 'failed'
          );

          setFiltersLoading(false);
        }


        // אם זו ביטול יזום,
        // לא להציג שגיאה
        if (e.name === 'AbortError') {
          return;
        }


        console.warn(
          'Filters API not available, options cleared:',
          e.message || e
        );

      }

    };


    tryFetch();


    return () => {

      cancelled = true;

      abortController.abort();

    };

  }, [genres]);


  // --------------------------------------------------
  // Tags
  // --------------------------------------------------

  const allTags = FILTER_TAGS;


  // --------------------------------------------------
  // סינון המוצרים
  // --------------------------------------------------

  const filteredItems = useMemo(() => {

    return items.filter(it => {

      // ----------------------------------------------
      // Tag filter
      // ----------------------------------------------

      if (selectedTags.length > 0) {

        const itTypes =
          (it.types || []).map(t => {

            if (typeof t === 'object') {
              return t.name || t.id;
            }

            return String(t);
          });


        const itTimings =
          (it.timings || []).map(t => {

            if (typeof t === 'object') {
              return t.name || t.id;
            }

            return String(t);
          });


        const all = [
          ...itTypes,
          ...itTimings
        ];


        const hasAny =
          selectedTags.some(
            tag => all.includes(tag)
          );


        if (!hasAny) {
          return false;
        }

      }


      // ----------------------------------------------
      // Video filter
      // ----------------------------------------------

      if (
        selectedVideo === 'with' &&
        !it.video
      ) {
        return false;
      }


      if (
        selectedVideo === 'without' &&
        it.video
      ) {
        return false;
      }


      // ----------------------------------------------
      // Price filter
      // ----------------------------------------------

      const itemPrice =
        Number(it.price);


      if (
        itemPrice < currentPriceRange[0] ||
        itemPrice > currentPriceRange[1]
      ) {
        return false;
      }


      return true;

    });

  }, [
    items,
    selectedTags,
    selectedVideo,
    currentPriceRange
  ]);


  // --------------------------------------------------
  // Render
  // --------------------------------------------------

  return (
    <>

      <PageHeader>
        זה קורה מידי פעם. עוצרים את שטף החיים, ומפגש מתרקם. עם החברות, עם המשפחה, עם הכיתה, עם הצוות או עם הקולגות.{'\n'}
        וצריך תוכנית.{'\n'}
        נו, צריך תוכנית!{'\n'}
        נו, מי עושה תוכנית???{'\n'}
        תני לנו למלא לך את המשבצת הזאת.{'\n'}
        הכנו לך מגוון של תוכניות, שיעניקו למפגש שלכן עושר של חוויה וגיבוש.{'\n'}
        שתי קליקים, והתוכנית אצלך 🙂 נוחה לתפעול, נעימה לקריאה, במעטפת יפההה (תודי שהגרפיקאית שלנו אלופה!).{'\n'}
        הנה, תסתכלי, ממש כאן את יכולה לדפדף בין המבחר 👇
      </PageHeader>


      <div className="programs-container">

        <div className="programs-frame">


          {/* ==========================================
              FILTERS
          ========================================== */}

          <div className="programs-filters">


            {/* שורה עליונה */}
            <div className="filters-row filters-row-top">


              {/* --------------------------------------
                  טווח מחירים
              -------------------------------------- */}

              <div className="filters-price">

                <div className="range-wrap">

                  <div className="range-track"></div>


                  <div
                    className="range-highlight"
                    style={highlightStyle}
                  />


                  {/* סליידר מינימום */}

                  <input
                    type="range"

                    min={priceBounds[0]}

                    max={priceBounds[1]}

                    step={1}

                    value={currentPriceRange[0]}

                    onChange={e => {

                      const newVal =
                        parseInt(
                          e.target.value,
                          10
                        );


                      if (
                        newVal <
                        currentPriceRange[1]
                      ) {

                        setPriceRange([
                          newVal,
                          currentPriceRange[1]
                        ]);

                      }

                    }}

                    className="range range-min"
                  />


                  {/* סליידר מקסימום */}

                  <input
                    type="range"

                    min={priceBounds[0]}

                    max={priceBounds[1]}

                    step={1}

                    value={currentPriceRange[1]}

                    onChange={e => {

                      const newVal =
                        parseInt(
                          e.target.value,
                          10
                        );


                      if (
                        newVal >
                        currentPriceRange[0]
                      ) {

                        setPriceRange([
                          currentPriceRange[0],
                          newVal
                        ]);

                      }

                    }}

                    className="range range-max"
                  />

                </div>


                {/* הצגת המחירים */}

                <div className="range-values">

                  <span>
                    {currentPriceRange[0]} ₪
                  </span>

                  <span>
                    {currentPriceRange[1]} ₪
                  </span>

                </div>

              </div>


              {/* --------------------------------------
                  פילטרים
              -------------------------------------- */}

              <div className="filters-tags">

                {allTags.map(tag => (

                  <button
                    key={tag}

                    className={`tag-btn ${
                      selectedTags.includes(tag)
                        ? 'active'
                        : ''
                    }`}

                    onClick={() => {

                      setSelectedTags(

                        selectedTags.includes(tag)

                          ? selectedTags.filter(
                              t => t !== tag
                            )

                          : [
                              ...selectedTags,
                              tag
                            ]

                      );

                    }}
                  >
                    {tag}
                  </button>

                ))}


                {/* עם מסך */}

                <button
                  className={`tag-btn ${
                    selectedVideo === 'with'
                      ? 'active'
                      : ''
                  }`}

                  onClick={() => {

                    setSelectedVideo(
                      selectedVideo === 'with'
                        ? null
                        : 'with'
                    );

                  }}
                >
                  עם מסך
                </button>


                {/* ללא מסך */}

                <button
                  className={`tag-btn ${
                    selectedVideo === 'without'
                      ? 'active'
                      : ''
                  }`}

                  onClick={() => {

                    setSelectedVideo(
                      selectedVideo === 'without'
                        ? null
                        : 'without'
                    );

                  }}
                >
                  ללא מסך
                </button>

              </div>

            </div>


            {/* --------------------------------------
                שורה תחתונה
            -------------------------------------- */}

            <div className="filters-row filters-row-bottom">


              {/* נקה מסננים */}

              <button
                className="filters-clear"

                onClick={() => {

                  setSelectedTags([]);

                  setSelectedVideo(null);

                  // חזרה לטווח המחירים המלא
                  setPriceRange(null);

                }}
              >
                נקה מסננים
              </button>


              {/* כמות תוצאות */}

              <div className="filters-count">

                מציג {filteredItems.length}
                {' '}
                מתוך
                {' '}
                {items.length}

              </div>

            </div>

          </div>


          {/* ==========================================
              CONTENT
          ========================================== */}

          <div className="filters-actions">
          </div>


          {/* Loading */}

          {loading && (
            <div className="programs-loading">
              ...חכי שניה, זה עולה
            </div>
          )}


          {/* Error */}

          {error && (
            <div className="programs-error">
              :) אופססססס, פשלה! איזה בושות
            </div>
          )}


          {/* ==========================================
              PRODUCTS
          ========================================== */}

          <div className="programs-grid">

            {filteredItems.map(it => (

              <ProductCard

                key={it.id}

                item={it}

                onOpen={openItem}

                onToggleFavorite={
                  toggleFavorite
                }

                isFavorite={
                  isItemFavorite(it)
                }

                showMinitsLabel={
                  it.genre === 'מיניטס'
                }

              />

            ))}

          </div>


          {/* אין תוצאות */}

          {!loading &&
            !error &&
            items.length === 0 && (

              <div className="programs-empty">
                חיפשנו, לא מצאנו כלום :(
              </div>

            )}

        </div>

      </div>

    </>
  );
};


export default Programs;